import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export const OAuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const handleCallback = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check for error parameter from backend redirect
        const errorParam = searchParams.get('error');
        if (errorParam) {
          throw new Error(`Authentication failed: ${errorParam}`);
        }

        // Backend has already processed OAuth callback and set HTTP-only cookie
        // Now we need to fetch the user profile from the backend
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
        
        const response = await fetch(`${API_URL}/users/me`, {
          method: 'GET',
          credentials: 'include', // Include HTTP-only cookies
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Authentication failed. Please try logging in again.');
          }
          throw new Error(`Failed to fetch user profile: ${response.statusText}`);
        }

        const user = await response.json();
        
        if (!isMounted) return;
        
        // Token is stored in HTTP-only cookie, so we don't need to pass it
        // Just set the user data in auth state
        // Note: We use a placeholder token since the real token is in HTTP-only cookie
        setAuth('cookie-based-token', user);
        
        if (!isMounted) return;
        
        // Redirect to intended route (saved in sessionStorage) or default to dashboard
        const intendedRoute = sessionStorage.getItem('auth_redirect') || '/';
        sessionStorage.removeItem('auth_redirect'); // Clear after use
        navigate(intendedRoute, { replace: true });
      } catch (err) {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setLoading(false);
      }
    };

    handleCallback();

    return () => {
      isMounted = false;
    };
  }, [searchParams, navigate, setAuth]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-base text-white">Completing authentication...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-[#CC0000]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-h2 font-bold text-text-primary mb-2">Authentication Failed</h2>
          <p className="text-base text-text-secondary mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/auth/login')}
              className="w-full h-11 bg-primary text-white rounded-md px-4 py-2 text-base font-medium hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
            >
              Return to Login
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full h-11 bg-white border-2 border-primary text-primary rounded-md px-4 py-2 text-base font-medium hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

